import { ShellIconPlay } from '@zrsystem/ui';
import { useEffect, useRef, useState, type ReactElement, type SyntheticEvent } from 'react';

function formatAttachVideoDuration(seconds: number): string | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

function primeVideoFrame(video: HTMLVideoElement): void {
  if (video.currentTime > 0) return;
  if (!Number.isFinite(video.duration) || video.duration <= 0) return;
  video.currentTime = Math.min(0.1, video.duration / 2);
}

/** Intrinsic video size ignores percentage max-height and overflows the stage. */
function fitVideoToFrame(video: HTMLVideoElement, frame: HTMLElement): void {
  if (video.videoWidth === 0 || video.videoHeight === 0) return;
  const scale = Math.min(
    frame.clientWidth / video.videoWidth,
    frame.clientHeight / video.videoHeight,
  );
  if (!Number.isFinite(scale) || scale <= 0) return;
  video.style.width = `${Math.floor(video.videoWidth * scale)}px`;
  video.style.height = `${Math.floor(video.videoHeight * scale)}px`;
}

export function ChatAttachVideoPreview({ src }: { readonly src: string }): ReactElement {
  const frameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    const frame = frameRef.current;
    if (video === null || frame === null) return;
    const media = video;
    const box = frame;
    function fit(): void {
      fitVideoToFrame(media, box);
    }
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(frame);
    video.addEventListener('loadedmetadata', fit);
    return () => {
      observer.disconnect();
      video.removeEventListener('loadedmetadata', fit);
    };
  }, []);

  function handleLoadedMetadata(event: SyntheticEvent<HTMLVideoElement>): void {
    primeVideoFrame(event.currentTarget);
    const frame = frameRef.current;
    if (frame !== null) fitVideoToFrame(event.currentTarget, frame);
  }

  return (
    <div className="chat-attach__video-frame" ref={frameRef}>
      <div className="chat-attach__video-stage">
        <video
          ref={videoRef}
          className="chat-attach__video"
          src={src}
          playsInline
          preload="metadata"
          controls={playing}
          controlsList="nodownload noplaybackrate noremoteplayback"
          disablePictureInPicture
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => {
            setPlaying(true);
          }}
          onPause={() => {
            setPlaying(false);
          }}
        />
        {playing ? null : (
          <button
            type="button"
            className="chat-attach__play"
            aria-label="Reproduzir vídeo"
            onClick={() => {
              void videoRef.current?.play();
            }}
          >
            <ShellIconPlay size={28} fill="currentColor" stroke="none" />
          </button>
        )}
      </div>
    </div>
  );
}

export function ChatAttachVideoThumb({ src }: { readonly src: string }): ReactElement {
  const [duration, setDuration] = useState<string | null>(null);

  function handleLoadedMetadata(event: SyntheticEvent<HTMLVideoElement>): void {
    const video = event.currentTarget;
    setDuration(formatAttachVideoDuration(video.duration));
    primeVideoFrame(video);
  }

  return (
    <span className="chat-attach__video-thumb">
      <video
        src={src}
        muted
        playsInline
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
      />
      <span className="chat-attach__play chat-attach__play--thumb" aria-hidden="true">
        <ShellIconPlay size={11} fill="currentColor" stroke="none" />
      </span>
      {duration !== null ? <span className="chat-attach__video-time">{duration}</span> : null}
    </span>
  );
}
